import { MenuItem, Typography } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useListContext,
  sanitizeFetchType,
  GET_MANY,
  GetManyResult,
  useTranslate,
} from 'react-admin'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { ApiErrorDto } from '../../../../core/dto/error-handling/api-error.dto'
import { DepositorDto } from '../../../../core/dto/device/depositor/depositor.dto'
import {
  ProgressStatus,
  StateBulkProgressDialog,
} from '../../../common/StateBulkProgressDialog'
import { BulkActionProps } from '../../../common/BulkActionsProps'
import { ConfirmDialog } from '../../../common/ConfirmDialog'

export const DepositorActionBulkButton = ({
  selectedIds,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  depositorAction,
  ...props
}: BulkActionProps & { depositorAction: 'openLocker' | 'releaseLocker' }) => {
  const { loading, refetch } = useListContext<DepositorDto>()
  const notify = useNotify()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [depositorActionDialogOpen, setDepositorActionDialogOpen] =
    useState(false)
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [bulkStateDialogOpen, setBulkStateDialogOpen] = useState(false)
  const [bulkProgressStatus, setBulkProgressStatus] = useState<
    ProgressStatus[]
  >([])
  const updateBulkProgressStatus = (newStatus: ProgressStatus) => {
    const oldStatusIndex = bulkProgressStatus.findIndex(
      (s) => s.id === newStatus.id,
    )
    bulkProgressStatus.splice(oldStatusIndex, 1, newStatus)
  }
  const [selectedDepositor, setSelectedDepositor] = useState<DepositorDto[]>([])

  const handleMutation = async (depositor: DepositorDto) => {
    let error
    const query = {
      type: sanitizeFetchType(depositorAction),
      resource: ResourceName.DEPOSITORS,
      payload: {
        id: depositor.id,
      },
    }
    let result = null
    try {
      result = await mutate(query, {
        returnPromise: true,
      })
    } catch (err) {
      error = err
    }
    if (error) {
      updateBulkProgressStatus({
        id: depositor.id,
        status: 'error',
        error: error as ApiErrorDto,
      } as ProgressStatus)
      return error
    }
    updateBulkProgressStatus({
      id: depositor.id,
      status: 'success',
    } as ProgressStatus)
    return result
  }

  const handleDepositorAction = async () => {
    setBulkStateDialogOpen(true)
    setDepositorActionDialogOpen(false)
    const pendingRequests: Promise<any>[] = []
    let selectedDepositorResponse
    try {
      selectedDepositorResponse = (await mutate(
        {
          type: sanitizeFetchType(GET_MANY),
          resource: ResourceName.DEPOSITORS,
          payload: {
            ids: selectedIds,
          },
        },
        { returnPromise: true, onFailure: (msg) => notify(msg, 'error') },
      )) as GetManyResult<DepositorDto>
    } catch (err) {
      setBulkStateDialogOpen(false)
      notify((err as Error)?.message, 'error')
      return
    }
    if (!selectedDepositorResponse.data.length) {
      setBulkStateDialogOpen(false)
      return
    }
    selectedDepositorResponse.data.forEach((depositor) => {
      pendingRequests.push(
        new Promise((resolve) => {
          setTimeout(async () => {
            await handleMutation(depositor)
            resolve(null)
          }, pendingRequests.length * 1000)
        }),
      )
      selectedDepositor.push(depositor)
      bulkProgressStatus.push({
        id: depositor.id,
        status: 'pending',
      } as ProgressStatus)
    })
    await Promise.all(pendingRequests)
    refetch()
  }

  useEffect(() => {
    if (!bulkStateDialogOpen) {
      setBulkProgressStatus([])
      setSelectedDepositor([])
    }
  }, [bulkStateDialogOpen])

  return (
    <MenuItem
      {...props}
      disabled={
        !selectedIds.length ||
        !hasAuthority(Authority.DEPOSITORS_OPEN_LOCKER_BUTTON) ||
        loading ||
        mutationLoading
      }
      onClick={() => setDepositorActionDialogOpen(true)}
      button
    >
      <Typography>
        {translate(`resources.depositors.actions.${depositorAction}`)}
      </Typography>
      <ConfirmDialog
        open={depositorActionDialogOpen}
        confirm={handleDepositorAction}
        source={depositorAction}
        cancel={() => setDepositorActionDialogOpen(false)}
        action="set"
        resource={ResourceName.DEPOSITORS}
      />
      <StateBulkProgressDialog
        open={bulkStateDialogOpen}
        close={() => setBulkStateDialogOpen(false)}
        elements={selectedDepositor}
        method={depositorAction}
        statuses={bulkProgressStatus}
        resource={ResourceName.DEPOSITORS}
      />
    </MenuItem>
  )
}
